import { Html, Line } from "@react-three/drei";
import { useFrame, useLoader, useThree } from "@react-three/fiber";
import { useEffect, useMemo, useRef } from "react";
import {
  AdditiveBlending,
  BackSide,
  Color,
  MathUtils,
  SRGBColorSpace,
  Vector3,
  TextureLoader
} from "three";
import type { Group } from "three";
import type {
  GeoChangeLayerId,
  GeoChangeSnapshot,
  GlobeViewState,
  RegionRecord,
  SpeciesRecord,
  WorldSnapshot,
  ZooSite
} from "../../domain/types";
import { continents, countries, scrollChapters, tradeRoutes, wildAreas } from "../../data";
import { CountryBoundaryLayer } from "./CountryBoundaryLayer";
import { GlobeLabelLayer } from "./GlobeLabelLayer";
import { TradeRouteLayer } from "./TradeRouteLayer";
import { EARTH_TEXTURE_ALIGNMENT, GLOBE_ROOT_ROTATION, latLonToVector3 } from "./spherical";
import { isSurfacePointFrontFacing } from "./surfaceVisibility";

type ProceduralGlobeProps = {
  snapshot: WorldSnapshot;
  regions: RegionRecord[];
  species: SpeciesRecord[];
  zoos: ZooSite[];
  globeView: GlobeViewState;
  activeGeoChangeLayer?: GeoChangeLayerId;
  geoChangeSnapshot?: GeoChangeSnapshot;
  selectedRegionId?: string;
  selectedSpeciesId?: string;
  selectedZooId?: string;
  onSelectRegion: (regionId?: string) => void;
  onSelectSpecies: (speciesId?: string) => void;
  onSelectZoo: (zooId?: string) => void;
};

const earthDayUrl = new URL("../../assets/earth/earth-day-noclouds.jpg", import.meta.url).href;
const earthCloudsUrl = new URL("../../assets/earth/earth-day-clouds.jpg", import.meta.url).href;
const earthNightUrl = new URL("../../assets/earth/earth-night-lights.jpg", import.meta.url).href;

export function ProceduralGlobe({
  snapshot,
  globeView,
  activeGeoChangeLayer,
  geoChangeSnapshot,
  regions,
  species,
  zoos,
  selectedRegionId,
  selectedSpeciesId,
  selectedZooId,
  onSelectRegion,
  onSelectSpecies,
  onSelectZoo
}: ProceduralGlobeProps) {
  const surface = snapshot.earthSurface;
  const activeView = globeView.activeView;
  const activeWildAreaIds = [
    selectedRegionId,
    selectedSpeciesId
      ? regions.find((region) =>
          species
            .find((record) => record.id === selectedSpeciesId)
            ?.regionIds.includes(region.id)
        )?.id
      : undefined
  ].filter((id): id is string => Boolean(id));
  const activeRouteIds =
    [...scrollChapters]
      .filter((chapter) => chapter.year <= snapshot.year)
      .at(-1)?.activeRouteIds ?? [];
  const { gl } = useThree();
  const [dayTexture, cloudTexture, nightTexture] = useLoader(TextureLoader, [
    earthDayUrl,
    earthCloudsUrl,
    earthNightUrl
  ]);

  const surfacePalette = useMemo(() => {
    const vegetation = clamp01(surface.vegetationIndex);
    const heat = clamp01(surface.oceanHeatIndex);
    const human = clamp01(surface.humanExposureIndex);
    const landColor = new Color(activeView === "topographic" ? "#f4e7c6" : "#d6c394");
    landColor.lerp(new Color("#72b96e"), activeView === "topographic" ? vegetation * 0.35 : vegetation);
    landColor.lerp(new Color("#b68e63"), human * 0.34);

    return {
      landColor,
      oceanColor: new Color("#2c7fa5").lerp(new Color("#5fb8dd"), 1 - heat),
      nightOpacity: MathUtils.lerp(0.1, 0.78, clamp01(surface.nightLightIndex)),
      cloudOpacity: MathUtils.lerp(0.12, 0.34, clamp01(surface.cloudOpacity)),
      waterOpacity: MathUtils.lerp(0.06, 0.2, heat),
      atmosphereOpacity: MathUtils.lerp(0.12, 0.22, clamp01(surface.weatherEnergyIndex))
    };
  }, [activeView, surface]);

  useEffect(() => {
    for (const texture of [dayTexture, cloudTexture, nightTexture]) {
      texture.colorSpace = SRGBColorSpace;
      texture.anisotropy = Math.min(12, gl.capabilities.getMaxAnisotropy());
      texture.offset.x = EARTH_TEXTURE_ALIGNMENT.longitudeOffsetDeg / 360;
      texture.repeat.x = EARTH_TEXTURE_ALIGNMENT.flipX ? -1 : 1;
      texture.needsUpdate = true;
    }
  }, [cloudTexture, dayTexture, gl, nightTexture]);

  return (
    <group rotation={[...GLOBE_ROOT_ROTATION]}>
      <mesh>
        <sphereGeometry args={[1, 192, 192]} />
        <meshStandardMaterial
          color={surfacePalette.landColor}
          map={dayTexture}
          bumpMap={dayTexture}
          bumpScale={
            activeView === "topographic"
              ? 0.022
              : MathUtils.lerp(0.006, 0.012, clamp01(surface.vegetationIndex))
          }
          roughness={activeView === "topographic" ? 0.96 : 0.88}
          metalness={0.03}
          emissive={activeView === "wild-evidence" ? "#03170e" : "#010b12"}
          emissiveIntensity={activeView === "wild-evidence" ? 0.16 : 0.08}
        />
      </mesh>

      {snapshot.layers.oceans ? (
        <mesh>
          <sphereGeometry args={[1.003, 192, 192]} />
          <meshStandardMaterial
            color={surfacePalette.oceanColor}
            transparent
            opacity={surfacePalette.waterOpacity}
            roughness={0.58}
            metalness={0.08}
          />
        </mesh>
      ) : null}

      {snapshot.layers.population || snapshot.layers.settlements ? (
        <mesh scale={1.006}>
          <sphereGeometry args={[1, 192, 192]} />
          <meshBasicMaterial
            map={nightTexture}
            color="#ffd89a"
            transparent
            opacity={surfacePalette.nightOpacity}
            blending={AdditiveBlending}
            depthWrite={false}
          />
        </mesh>
      ) : null}

      {snapshot.layers.atmosphere ? (
        <mesh scale={1.011}>
          <sphereGeometry args={[1, 192, 192]} />
          <meshBasicMaterial
            map={cloudTexture}
            color="#ffffff"
            transparent
            opacity={surfacePalette.cloudOpacity}
            blending={AdditiveBlending}
            depthWrite={false}
          />
        </mesh>
      ) : null}

      {snapshot.layers.topographicRelief || activeView === "topographic" ? (
        <TopographicReliefLines />
      ) : null}

      {snapshot.layers.adminBoundaries || activeView === "admin-regions" ? (
        <CountryBoundaryLayer countries={countries} zoomScalar={globeView.zoomScalar} />
      ) : null}

      <GlobeLabelLayer
        continents={continents}
        countries={countries}
        wildAreas={wildAreas}
        zoomScalar={globeView.zoomScalar}
        currentYear={snapshot.year}
        activeWildAreaIds={activeWildAreaIds}
        viewLat={globeView.latitudeDeg}
        viewLon={globeView.longitudeDeg}
        renderMode="three"
      />

      <TradeRouteLayer
        routes={tradeRoutes}
        currentYear={snapshot.year}
        activeRouteIds={activeRouteIds}
      />

      {activeView === "wild-evidence" || snapshot.layers.wildSpace ? (
        <WildEvidenceRings activeView={activeView} />
      ) : null}

      {activeGeoChangeLayer && geoChangeSnapshot ? (
        <GeoChangeGlobeOverlay
          activeLayerId={activeGeoChangeLayer}
          geoChangeSnapshot={geoChangeSnapshot}
        />
      ) : null}

      {snapshot.layers.atmosphere ? (
        <mesh scale={1.085}>
          <sphereGeometry args={[1, 128, 128]} />
          <meshBasicMaterial
            color="#7ad7ff"
            side={BackSide}
            transparent
            opacity={surfacePalette.atmosphereOpacity}
            blending={AdditiveBlending}
          />
        </mesh>
      ) : null}

      {snapshot.layers.species
        ? regions.map((region) => {
            const regionSpecies = species.filter((record) =>
              record.regionIds.includes(region.id)
            );
            const isSelected =
              selectedRegionId === region.id ||
              regionSpecies.some((record) => record.id === selectedSpeciesId);

            return (
              <GlobeMarker
                key={region.id}
                lat={region.lat}
                lon={region.lon}
                color={isSelected ? "#f4d35e" : "#89e6a2"}
                label={region.name}
                selected={isSelected}
                testId={`region-marker-${region.id}`}
                onClick={() => {
                  onSelectRegion(region.id);
                  onSelectSpecies(regionSpecies[0]?.id);
                }}
              />
            );
          })
        : null}

      {snapshot.layers.zoos
        ? zoos
            .filter((zoo) => !zoo.openedYear || zoo.openedYear <= snapshot.year)
            .map((zoo) => (
              <GlobeMarker
                key={zoo.id}
                lat={zoo.lat}
                lon={zoo.lon}
                color={selectedZooId === zoo.id ? "#ff8b7a" : "#f3c26b"}
                label={zoo.name}
                selected={selectedZooId === zoo.id}
                testId={`zoo-marker-${zoo.id}`}
                onClick={() => onSelectZoo(zoo.id)}
              />
            ))
        : null}
    </group>
  );
}

function clamp01(value: number) {
  return Math.max(0, Math.min(1, value));
}

const topographicPaths = [
  [
    [35, 70],
    [31, 82],
    [29, 91],
    [31, 101],
    [34, 111]
  ],
  [
    [45, -124],
    [39, -116],
    [35, -106],
    [31, -101]
  ],
  [
    [8, -78],
    [-8, -75],
    [-18, -71],
    [-32, -70],
    [-48, -72]
  ],
  [
    [45, 6],
    [46, 11],
    [47, 17],
    [45, 24]
  ],
  [
    [-4, 29],
    [-14, 34],
    [-25, 29],
    [-31, 25]
  ]
] satisfies Array<Array<[number, number]>>;

const wildEvidenceRegions = [
  { label: "Amazon low human footprint", lat: -4, lon: -62, radius: 14, color: "#72d582" },
  { label: "Congo forest core", lat: -1.2, lon: 21.5, radius: 12, color: "#64c799" },
  { label: "Borneo land-cover context", lat: 0.9, lon: 114.4, radius: 9, color: "#a7c85a" },
  { label: "Boreal intact forest", lat: 58, lon: 92, radius: 18, color: "#8fd7c3" },
  { label: "Arctic protected context", lat: 72, lon: -42, radius: 13, color: "#9fd8ff" }
];

function TopographicReliefLines() {
  return (
    <group>
      {topographicPaths.map((points, index) => (
        <Line
          key={`relief-${index}`}
          points={points.map(([lat, lon]) => latLonToVector3(lat, lon, 1.04))}
          color="#e9cf86"
          lineWidth={1}
          transparent
          opacity={0.46}
        />
      ))}
    </group>
  );
}

function WildEvidenceRings({ activeView }: { activeView: GlobeViewState["activeView"] }) {
  const opacity = activeView === "wild-evidence" ? 0.82 : 0.24;

  return (
    <group>
      {wildEvidenceRegions.map((region) => (
        <Line
          key={region.label}
          points={makeLatLonRing(region.lat, region.lon, region.radius).map(([lat, lon]) =>
            latLonToVector3(lat, lon, 1.048)
          )}
          color={region.color}
          lineWidth={activeView === "wild-evidence" ? 1.8 : 0.8}
          transparent
          opacity={opacity}
        />
      ))}
    </group>
  );
}

function GeoChangeGlobeOverlay({
  activeLayerId,
  geoChangeSnapshot
}: {
  activeLayerId: GeoChangeLayerId;
  geoChangeSnapshot: GeoChangeSnapshot;
}) {
  const activeLayer = geoChangeSnapshot.layers.find((layer) => layer.id === activeLayerId);

  if (!activeLayer) {
    return null;
  }

  const haloRegions = [...geoChangeSnapshot.regions]
    .sort(
      (first, second) =>
        second.layerIntensities[activeLayerId] - first.layerIntensities[activeLayerId]
    )
    .slice(0, 6);

  return (
    <group>
      {haloRegions.map((region) => {
        const intensity = region.layerIntensities[activeLayerId];
        const radiusDeg = Math.max(5, Math.min(18, region.radiusKm / 110));

        return (
          <Line
            key={`${activeLayerId}-${region.regionId}`}
            points={makeLatLonRing(region.lat, region.lon, radiusDeg).map(([lat, lon]) =>
              latLonToVector3(lat, lon, 1.056)
            )}
            color={activeLayer.color}
            lineWidth={MathUtils.lerp(0.7, 2.15, intensity)}
            transparent
            opacity={MathUtils.lerp(0.24, 0.84, intensity)}
          />
        );
      })}
    </group>
  );
}

function makeLatLonRing(lat: number, lon: number, radiusDeg: number) {
  const points: Array<[number, number]> = [];

  for (let index = 0; index <= 48; index += 1) {
    const angle = (index / 48) * Math.PI * 2;
    points.push([
      lat + Math.sin(angle) * radiusDeg * 0.55,
      lon + Math.cos(angle) * radiusDeg
    ]);
  }

  return points;
}

type GlobeMarkerProps = {
  lat: number;
  lon: number;
  label: string;
  color: string;
  selected: boolean;
  testId: string;
  onClick: () => void;
};

function GlobeMarker({
  lat,
  lon,
  label,
  color,
  selected,
  testId,
  onClick
}: GlobeMarkerProps) {
  const groupRef = useRef<Group>(null);
  const surfaceWorld = useMemo(() => new Vector3(), []);
  const cameraWorld = useMemo(() => new Vector3(), []);
  const position = latLonToVector3(lat, lon, selected ? 1.125 : 1.105);
  const { camera } = useThree();

  useFrame(() => {
    const group = groupRef.current;

    if (!group) {
      return;
    }

    group.getWorldPosition(surfaceWorld);
    camera.getWorldPosition(cameraWorld);
    group.visible = isSurfacePointFrontFacing(surfaceWorld, cameraWorld);
  });

  return (
    <group ref={groupRef} position={position}>
      <mesh
        data-testid={testId}
        scale={selected ? 0.024 : 0.017}
        onClick={(event) => {
          event.stopPropagation();
          onClick();
        }}
      >
        <sphereGeometry args={[1, 18, 18]} />
        <meshStandardMaterial
          color={color}
          emissive={color}
          emissiveIntensity={selected ? 0.48 : 0.22}
          roughness={0.35}
        />
      </mesh>
      {selected ? (
        <Html center distanceFactor={9.2} className="globe-marker-label">
          {label}
        </Html>
      ) : null}
    </group>
  );
}
